// MEO landmark ads (Phase 1, Mapbox UX 改修 案 D, 2026-05-01):
// 統合運用 map ページ。items + landmarks を同時に Mapbox 上に表示。
//
// PoC オペレータが「沖縄那覇 PoC で配置漏れがないか」「両替機 (緑) の周辺に商業
// ポストカード (オレンジ) があるか」を一目で点検する用途。
//
// auth: ADMIN (全 advertiser) / BUSINESS (自分が advertiser_user_id の items のみ)。
// CMS 側は role 判定不要、backend が自動フィルタ。

import { useStore } from '@/hooks/useStore';
import { observer } from 'mobx-react-lite';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { flowResult } from 'mobx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_PUBKEY ?? '';
if (MAPBOX_TOKEN) {
    mapboxgl.accessToken = MAPBOX_TOKEN;
}

// 沖縄 PoC エリア中心 (那覇国際通り付近)
const DEFAULT_CENTER: [number, number] = [127.681, 26.214];
const DEFAULT_ZOOM = 11;

const ITEM_LIMIT = 1000;
const LANDMARK_LIMIT = 1000;

// pin 色 (CSS で適用)
const PIN_STYLE = {
    item_personal: '#3B82F6', // 青: 個人 item
    item_commercial: '#F97316', // オレンジ: 商業ポストカード
    landmark_currency: '#10B981', // 緑: 両替機
    landmark_other: '#6B7280' // グレー: その他 landmark
};

const makePinElement = (color: string, size = 14): HTMLDivElement => {
    const el = document.createElement('div');
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    el.style.borderRadius = '50%';
    el.style.background = color;
    el.style.border = '2px solid white';
    el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.4)';
    return el;
};

// マップ非表示 (イベント用) アイテムの pin (2026-07-23)。
// 旧実装は「半透明 + 破線枠」だったが 14px では判別できないと Boss 指摘。
// 通常 pin より一回り大きくし、禁止標識と同じ「斜線入り」にして形で区別する。
// 種別色は保持するので 個人/商業 の区別も同時に読める。
const makeHiddenPinElement = (color: string, size = 20): HTMLDivElement => {
    const el = document.createElement('div');
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    el.style.filter = 'drop-shadow(0 1px 4px rgba(0,0,0,0.5))';
    el.innerHTML = `
        <svg width="${size}" height="${size}" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="8" fill="${color}" stroke="#FFFFFF" stroke-width="2" />
            <line x1="5.2" y1="14.8" x2="14.8" y2="5.2" stroke="#FFFFFF" stroke-width="2.6" stroke-linecap="round" />
        </svg>
    `;
    return el;
};

export default observer(function OperationsMapPage() {
    const { t } = useTranslation();
    const {
        itemStore: { getItemsForMap },
        landmarkStore: { list: listLandmarks }
    } = useStore();

    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const markersRef = useRef<mapboxgl.Marker[]>([]);

    const [items, setItems] = useState<any[]>([]);
    const [landmarks, setLandmarks] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [showItems, setShowItems] = useState(true);
    const [showLandmarks, setShowLandmarks] = useState(true);

    // map 初期化 (mount 時のみ)
    useEffect(() => {
        if (!MAPBOX_TOKEN || !containerRef.current || mapRef.current) return;
        const map = new mapboxgl.Map({
            container: containerRef.current,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: DEFAULT_CENTER,
            zoom: DEFAULT_ZOOM
        });
        // 拡大縮小 (+/-) と方位リセット。トラックパッド無し環境でも操作できるように (2026-07-23)
        map.addControl(new mapboxgl.NavigationControl({ visualizePitch: false }), 'top-right');
        // 縮尺バー: ピン間の距離感を掴めるように
        map.addControl(new mapboxgl.ScaleControl({ maxWidth: 120, unit: 'metric' }), 'bottom-left');
        mapRef.current = map;
        return () => {
            map.remove();
            mapRef.current = null;
        };
    }, []);

    // データ取得
    const reload = async () => {
        setLoading(true);
        try {
            const [itemsRes, landmarksRes] = await Promise.all([
                // 旧: getAllItem({ limit }) は API が limit を読まず既定 10 件しか描画されなかった (2026-09-17 fix)
                flowResult(getItemsForMap(ITEM_LIMIT)),
                flowResult(listLandmarks({ limit: LANDMARK_LIMIT }))
            ]);
            const itemsList = itemsRes?.data?.elements ?? [];
            const landmarksList = landmarksRes?.data?.items ?? [];
            setItems(itemsList);
            setLandmarks(landmarksList);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // マーカー再描画 (data / toggle 変更時)
    useEffect(() => {
        if (!mapRef.current) return;
        // 既存マーカー削除
        markersRef.current.forEach(m => m.remove());
        markersRef.current = [];

        const bounds = new mapboxgl.LngLatBounds();
        let hasAny = false;

        if (showItems) {
            items.forEach((it) => {
                const lat = Number(it.latitude);
                const lng = Number(it.longitude);
                if (!isFinite(lat) || !isFinite(lng) || (lat === 0 && lng === 0)) return;
                // findAllItem は fileInfoMapper 経由 (camelCase: item / itemType) で返す。
                // 旧コードは raw snake_case (title / item_type) を読んでいて常に
                // 「(無題)」「個人」表示になっていたため両対応で fix (2026-07-22)。
                const isCommercial = Number(it.itemType ?? it.item_type) === 1;
                const isHidden = (it.hiddenOnMap ?? it.hidden_on_map) === true;
                const color = isCommercial ? PIN_STYLE.item_commercial : PIN_STYLE.item_personal;
                const popup = new mapboxgl.Popup({ offset: 12 }).setHTML(
                    `<div style="font-size:12px;line-height:1.4">
                        <div><b>${escapeHtml(it.item || it.title || '(無題)')}</b></div>
                        <div style="color:#666">item_type: ${isCommercial ? '商業ポストカード' : '個人'}</div>
                        <div style="color:#666">id: ${it.id}</div>
                        ${isHidden ? '<div style="color:#B45309"><b>マップ非表示 (イベント用)</b> — enduser には表示されません</div>' : ''}
                    </div>`
                );
                // マップ非表示 (イベント用) は斜線入りの大きめ pin で区別する
                const pinEl = isHidden ? makeHiddenPinElement(color) : makePinElement(color);
                const marker = new mapboxgl.Marker({ element: pinEl })
                    .setLngLat([lng, lat])
                    .setPopup(popup)
                    .addTo(mapRef.current!);
                markersRef.current.push(marker);
                bounds.extend([lng, lat]);
                hasAny = true;
            });
        }

        if (showLandmarks) {
            landmarks.forEach((lm) => {
                const lat = Number(lm.latitude);
                const lng = Number(lm.longitude);
                if (!isFinite(lat) || !isFinite(lng)) return;
                const color = lm.category === 'currency_exchange' ?
                    PIN_STYLE.landmark_currency :
                    PIN_STYLE.landmark_other;
                const popup = new mapboxgl.Popup({ offset: 14 }).setHTML(
                    `<div style="font-size:12px;line-height:1.4">
                        <div><b>${escapeHtml(lm.name || '(無名)')}</b></div>
                        <div style="color:#666">${escapeHtml(lm.category || '-')}</div>
                        <div style="color:#666">id: ${lm.id}, radius: ${lm.radius_m ?? '-'}m</div>
                    </div>`
                );
                const marker = new mapboxgl.Marker({ element: makePinElement(color, 18) })
                    .setLngLat([lng, lat])
                    .setPopup(popup)
                    .addTo(mapRef.current!);
                markersRef.current.push(marker);
                bounds.extend([lng, lat]);
                hasAny = true;
            });
        }

        // 全データを画面に収める (1 件以上ある場合のみ)
        if (hasAny && !bounds.isEmpty()) {
            try {
                mapRef.current.fitBounds(bounds, { padding: 60, maxZoom: 14, duration: 0 });
            } catch (_e) {
                // bounds が極端なケースは無視
            }
        }
    }, [items, landmarks, showItems, showLandmarks]);

    if (!MAPBOX_TOKEN) {
        return (
            <div className='layout-card'>
                <div className='border border-orange-300 bg-orange-50 text-orange-800 p-4 rounded-md text-sm'>
                    <b>⚠️ Map disabled</b>
                    {' '}
                    — REACT_APP_MAPBOX_PUBKEY が設定されていません。
                    GitHub Secret \`ENV_FILE_PRODUCTION\` に追記してください。
                </div>
            </div>
        );
    }

    return (
        <div className='space-y-3'>
            <div className='layout-card'>
                <div className='flex flex-wrap items-center gap-3'>
                    <h2 className='text-[16px] font-bold mr-3'>
                        {t('words_title.operations_map') || '運用マップ'}
                    </h2>
                    <label className='flex items-center gap-1 text-sm'>
                        <input
                            type='checkbox'
                            checked={showItems}
                            onChange={e => setShowItems(e.target.checked)}
                        />
                        <span style={{ color: PIN_STYLE.item_personal }}>●</span>
                        個人
                        <span style={{ color: PIN_STYLE.item_commercial, marginLeft: '8px' }}>●</span>
                        商業ポストカード
                    </label>
                    <span className='flex items-center gap-1 text-sm text-gray-600'>
                        <svg width='16' height='16' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'>
                            <circle cx='10' cy='10' r='8' fill={PIN_STYLE.item_personal} stroke='#FFFFFF' strokeWidth='2' />
                            <line x1='5.2' y1='14.8' x2='14.8' y2='5.2' stroke='#FFFFFF' strokeWidth='2.6' strokeLinecap='round' />
                        </svg>
                        斜線入り = マップ非表示 (イベント用)
                    </span>
                    <label className='flex items-center gap-1 text-sm'>
                        <input
                            type='checkbox'
                            checked={showLandmarks}
                            onChange={e => setShowLandmarks(e.target.checked)}
                        />
                        <span style={{ color: PIN_STYLE.landmark_currency }}>●</span>
                        両替機
                        <span style={{ color: PIN_STYLE.landmark_other, marginLeft: '8px' }}>●</span>
                        その他ランドマーク
                    </label>
                    <button
                        type='button'
                        className='ml-auto px-3 py-1 border rounded-md text-sm hover:bg-gray-50'
                        onClick={reload}
                        disabled={loading}
                    >
                        {loading ? '読み込み中…' : '再読込'}
                    </button>
                    <span className='text-sm text-gray-500'>
                        {`items: ${items.length} / landmarks: ${landmarks.length}`}
                    </span>
                </div>
            </div>

            <div
                ref={containerRef}
                className='rounded-md overflow-hidden border'
                style={{ height: 'calc(100vh - 220px)', minHeight: '480px' }}
            />
        </div>
    );
});

const escapeHtml = (s: string): string => {
    const div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
};
